/**
 * Reconciliation of persisted localStorage state against the current defaults.
 *
 * Persisted state outlives the code that wrote it: a user's cache can hold a
 * shape from any release they've loaded before. Handing that value back to the
 * app as-is means a renamed key (`employeeNameFilter` -> `employeeFilter`)
 * reads as `undefined` and takes the page down on the first property access.
 *
 * `reconcileWithDefaults` treats the defaults as the schema:
 * - keys missing from the persisted value fall back to their default, so keys
 *   added by a later release are always present
 * - keys absent from the defaults are dropped, so keys a rename left behind
 *   don't linger (and don't make change tracking report a phantom change)
 * - values whose shape disagrees with the default — a Set where a string is
 *   expected, and so on — fall back to the default, so a key that was reused
 *   for a different type is safe to read
 *
 * Plain objects are reconciled recursively. Sets, Maps and arrays are taken
 * whole; their contents are the feature's business, not the schema's.
 */

export const isPlainObject = (
  value: unknown,
): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !(value instanceof Set) &&
  !(value instanceof Map)

const matchesShapeOf = (value: unknown, defaultValue: unknown): boolean => {
  if (defaultValue instanceof Set) return value instanceof Set
  if (defaultValue instanceof Map) return value instanceof Map
  if (Array.isArray(defaultValue)) return Array.isArray(value)
  if (isPlainObject(defaultValue)) return isPlainObject(value)
  // A null/undefined default says nothing about the shape it expects, so
  // anything persisted under that key is as good a guess as the default.
  if (defaultValue === null || defaultValue === undefined) return true
  return typeof value === typeof defaultValue
}

export const reconcileWithDefaults = <T extends object>(
  persisted: unknown,
  defaults: T,
): T => {
  if (!isPlainObject(persisted)) return defaults

  return Object.fromEntries(
    Object.entries(defaults).map(([key, defaultValue]) => {
      const persistedValue = persisted[key]

      if (!(key in persisted) || !matchesShapeOf(persistedValue, defaultValue))
        return [key, defaultValue]

      if (isPlainObject(defaultValue))
        return [key, reconcileWithDefaults(persistedValue, defaultValue)]

      return [key, persistedValue]
    }),
  ) as T
}
